// WHO the queued work belongs to (spec 06 §9.2, D-048 — fix round).
//
// A floor tablet is a SHARED device. Shift change is the normal case, not an
// edge case: worker A queues a cut in a dead zone, the app is killed by the OS
// (routine on a PWA phone), worker B signs in on the same tablet, and the
// hydrated queue drains under B's session cookie. The ledger movement and the
// append-only audit row are then stamped with B as the actor — a permanent,
// unverifiable attribution error in the one table that is supposed to be the
// record of who did what.
//
// The client cannot learn its own principal id: `GET /v1/auth/tenants` returns
// memberships, not a subject, and there is no `/v1/auth/me`. What it CAN record
// is the identity the operator signed in WITH, at the moment they signed in. So
// every outbox record is stamped with that, and the drain refuses any record
// whose stamp disagrees with the current one. That is a conservative check:
//   · it can never send A's work as B (the failure that corrupts the ledger);
//   · at worst it holds work back and says so, which the tray makes visible and
//     recoverable — sign in as A and it drains.
//
// Records with NO stamp (queued before this shipped, or on a device that has
// never been through the sign-in screen this install) are treated as the
// current principal's: stranding a shift's work over a missing tag would be a
// worse failure than the one being prevented, and the logout purge below is
// what actually covers hand-over on the normal path.

const PRINCIPAL_KEY = "assessly.floor.principal";

function safeStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

/** The identity the last sign-in on this device used, if it was observed. */
export function currentFloorPrincipal(): string | undefined {
  const storage = safeStorage();
  if (storage === undefined) return undefined;
  try {
    const value = storage.getItem(PRINCIPAL_KEY);
    return value !== null && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

/** Record (or clear) the signed-in identity. Called from the sign-in/out paths. */
export function setFloorPrincipal(identity: string | undefined): void {
  const storage = safeStorage();
  if (storage === undefined) return;
  try {
    const normalised = identity?.trim().toLowerCase();
    if (normalised === undefined || normalised.length === 0) storage.removeItem(PRINCIPAL_KEY);
    else storage.setItem(PRINCIPAL_KEY, normalised);
  } catch {
    // Non-fatal: an unstamped queue behaves exactly as it did before this
    // check existed, and the logout purge still covers the normal hand-over.
  }
}

/**
 * Does this record belong to whoever is signed in now?
 *
 * Unstamped records and an unknown current principal both answer YES — see the
 * header on why refusing to send is only the right default when we positively
 * know the two identities differ.
 */
export function isOwnPrincipal(
  recordPrincipal: string | undefined,
  current: string | undefined = currentFloorPrincipal(),
): boolean {
  if (recordPrincipal === undefined || current === undefined) return true;
  return recordPrincipal === current;
}
